// src/pages/Admin/AdminDashboard.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  Eye, 
  Heart, 
  MessageSquare, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle,
  Clock,
  BarChart3,
  Shield
} from 'lucide-react';
import adminService, { type DashboardStats } from '../../services/adminService';
import '../../utils/adminTestHelper'; // Import test helper for console access

interface StatsCardProps {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'increase' | 'decrease' | 'neutral';
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red';
}

const StatsCard: React.FC<StatsCardProps> = ({ title, value, change, changeType, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-orange-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500'
  };

  const changeColors = {
    increase: 'text-green-600',
    decrease: 'text-red-600',
    neutral: 'text-gray-600'
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          {change && (
            <p className={`text-sm ${changeColors[changeType || 'neutral']}`}>
              {change}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full ${colorClasses[color]}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const data = await adminService.getDashboardStats();
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading dashboard</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button
              onClick={loadDashboardStats}
              className="mt-2 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No dashboard data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="px-1">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-2 text-sm sm:text-base text-gray-600">Welcome back! Here's what's happening on your platform.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatsCard
          title="Total Users"
          value={stats.total_users.toLocaleString()}
          change={`+${stats.new_users_today} today`}
          changeType="increase"
          icon={Users}
          color="blue"
        />
        
        <StatsCard
          title="Total Content"
          value={stats.total_all_content.toLocaleString()}
          change={`${stats.verified_users} verified`}
          changeType="neutral"
          icon={FileText}
          color="green"
        />
        
        <StatsCard
          title="Total Views"
          value={stats.total_views.toLocaleString()}
          change={`${stats.active_users_today} active today`}
          changeType="increase"
          icon={Eye}
          color="purple"
        />
        
        <StatsCard
          title="Pending Moderation"
          value={stats.pending_moderation}
          change="Requires attention"
          changeType={stats.pending_moderation > 10 ? 'decrease' : 'neutral'}
          icon={AlertCircle}
          color={stats.pending_moderation > 10 ? 'red' : 'orange'}
        />
      </div>

      {/* User Statistics */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">User Statistics</h2>
          <Users className="h-5 w-5 text-gray-400" />
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-2">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <p className="text-2xl font-semibold text-gray-900">{stats.total_users.toLocaleString()}</p>
            <p className="text-sm text-gray-600">Total Users</p>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mx-auto mb-2">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <p className="text-2xl font-semibold text-gray-900">{stats.verified_users.toLocaleString()}</p>
            <p className="text-sm text-gray-600">Verified</p>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-orange-100 rounded-lg mx-auto mb-2">
              <Clock className="h-6 w-6 text-orange-600" />
            </div>
            <p className="text-2xl font-semibold text-gray-900">{stats.pending_users.toLocaleString()}</p>
            <p className="text-sm text-gray-600">Pending</p>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-orange-100 rounded-lg mx-auto mb-2">
              <Shield className="h-6 w-6 text-orange-600" />
            </div>
            <p className="text-2xl font-semibold text-gray-900">{stats.admin_users.toLocaleString()}</p>
            <p className="text-sm text-gray-600">Admin Users</p>
          </div>
        </div>
      </div>

      {/* Content Type Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Content Statistics */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Content by Type</h2>
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">All Originals</span>
              <span className="text-sm font-semibold text-orange-600">{stats.total_originals}</span>
            </div>
            <hr className="my-2" />
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">• Cinema</span>
              <span className="text-sm font-medium">{stats.total_cinema}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">• Contents</span>
              <span className="text-sm font-medium">{stats.total_content}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">• Podcasts</span>
              <span className="text-sm font-medium">{stats.total_podcasts}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">• Animations</span>
              <span className="text-sm font-medium">{stats.total_animations}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">• Sneak Peeks</span>
              <span className="text-sm font-medium">{stats.total_sneak_peeks}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">• Live Videos</span>
              <span className="text-sm font-medium">{stats.total_live_videos}</span>
            </div>
          </div>
        </div>

        {/* Engagement Overview */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Engagement Overview</h2>
            <TrendingUp className="h-5 w-5 text-gray-400" />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg mx-auto mb-2">
                <Eye className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              </div>
              <p className="text-lg sm:text-2xl font-semibold text-gray-900">{stats.total_views.toLocaleString()}</p>
              <p className="text-xs sm:text-sm text-gray-600">Views</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-lg mx-auto mb-2">
                <Heart className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
              </div>
              <p className="text-lg sm:text-2xl font-semibold text-gray-900">{stats.total_likes.toLocaleString()}</p>
              <p className="text-xs sm:text-sm text-gray-600">Likes</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg mx-auto mb-2">
                <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
              </div>
              <p className="text-lg sm:text-2xl font-semibold text-gray-900">{stats.total_comments.toLocaleString()}</p>
              <p className="text-xs sm:text-sm text-gray-600">Comments</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities and Trending Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Activities */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Recent Activities</h2>
            <Clock className="h-5 w-5 text-gray-400" />
          </div>
          
          <div className="space-y-3">
            {stats.recent_activities && stats.recent_activities.length > 0 ? (
              stats.recent_activities.slice(0, 5).map((activity, index) => (
                <div key={activity.id || index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-1" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{activity.description}</p>
                    <p className="text-xs text-gray-500">
                      by {activity.admin.username} • {new Date(activity.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No recent activities</p>
            )}
          </div>
        </div>

        {/* Trending Content */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Top Performing Content</h2>
            <TrendingUp className="h-5 w-5 text-gray-400" />
          </div>
          
          <div className="space-y-3">
            {stats.trending_originals && stats.trending_originals.length > 0 ? (
              stats.trending_originals.slice(0, 5).map((content, index) => (
                <div key={content.id || index} className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{content.title}</p>
                    <p className="text-xs text-gray-500">by {content.author__username}</p>
                  </div>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>{content.read_count} reads</span>
                    <span>{content.like_count} likes</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No trending content</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <button 
            onClick={() => navigate('/admin/users')}
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors"
          >
            <div className="text-center">
              <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900">Manage Users</p>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/admin/content')}
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors"
          >
            <div className="text-center">
              <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900">Review Content</p>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/admin/analytics')}
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors"
          >
            <div className="text-center">
              <BarChart3 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900">View Analytics</p>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/admin/moderation')}
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors"
          >
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900">Moderation Queue</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;